// Central registry of CleverTap Remote Config variables (feature flags).
//
// Each flag is defined on the SDK at init time with the local default below,
// then resolved from the server via fetchVariables(). Read values with
// useFlag(), which re-renders when the fetched value lands — a plain read
// returns whatever the SDK holds at that instant and never updates.
//
// NOTE: a variable's name is immutable once created in the CleverTap dashboard,
// so these names must match Remote Config exactly. Screens pass the constant
// (FEATURE_FLAG.SOMETHING) and never the raw string, so each name exists in
// exactly one place — renaming a variable is a one-line change here.

export interface FeatureFlagDefinition {
  /** Variable name as created in CleverTap Remote Config. */
  name: string;
  /** Local fallback used until server values are fetched. */
  defaultValue: string | number | boolean;
}

export const FEATURE_FLAG = {
  /**
   * Price-display (framing) experiment: template for the monthly Home Security
   * price text. Display-only — it never changes the amount charged.
   *
   * The value holds a FORMAT, never money, so it stays correct in every
   * currency and cannot drift from the real price. Tokens are resolved against
   * the live plan prices — see pages/v1/HomeBuySubscriptionV1/priceText.ts:
   *   {price} — the plan's own price, formatted for the user's locale
   *   {delta} — that price minus the cheapest monthly plan on the screen
   *
   * Control is `{price}`; the first variant is `+{delta}`.
   */
  MONTHLY_HOME_SECURITY_PRICE_FORMAT: {
    name: 'monthly-home-security-price-format',
    defaultValue: '{price}'
  },

  /**
   * Whole-screen experiment on the Home Security plan list: which screen a
   * user gets. Value is a variant key from EXPERIMENTS.PLAN_SELECTION
   * (src/experiments/registry.tsx) — `control` or `v2`. Unknown values render
   * control.
   */
  PLAN_SELECTION_SCREEN: {
    name: 'exp-plan-selection',
    defaultValue: 'control'
  },

  /**
   * Whole-screen experiment on the Home Security promotion page (after camera
   * naming): which page a user gets. Value is a variant key from
   * EXPERIMENTS.PROMOTION (src/experiments/registry.tsx) — `control` or `v2`.
   * Unknown values render control.
   */
  PROMOTION_SCREEN: {
    name: 'exp-promotion-screen',
    defaultValue: 'control'
  }
} as const;

/** Any flag in the registry above — what useFlag() accepts. */
export type FeatureFlag = (typeof FEATURE_FLAG)[keyof typeof FEATURE_FLAG];

// Every flag registered on the SDK at initialize time.
export const FEATURE_FLAG_DEFINITIONS: readonly FeatureFlagDefinition[] =
  Object.values(FEATURE_FLAG);
